/**
 * Building the outbound request for each notification-channel type.
 *
 * A channel routes an alert somewhere — a Slack/Teams/Discord/Telegram chat, a generic
 * webhook, or a PagerDuty service. What differs between them is only the destination
 * URL, the request body shape, and (for PagerDuty/Telegram) where the secret goes. This
 * module is that difference, kept pure: given the channel's secret, its non-secret
 * config, and a normalized alert payload, it returns the exact HTTP request to send.
 * The transport (the actual POST) and the secret's storage live in the controller; the
 * shape of every provider's payload lives here so it is one place to read and to test.
 */

export const NOTIFICATION_CHANNEL_TYPES = [
  'webhook',
  'slack',
  'teams',
  'discord',
  'telegram',
  'pagerduty',
] as const;
export type NotificationChannelType = (typeof NOTIFICATION_CHANNEL_TYPES)[number];

export type NotificationSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

/** The normalized alert a channel renders. Provider-agnostic. */
export interface NotificationPayload {
  severity: NotificationSeverity;
  /** OPEN / ACKNOWLEDGED / RESOLVED — drives trigger vs resolve semantics where a provider has them. */
  status: string;
  title: string;
  description?: string;
  /** Where the alert came from, e.g. `rule:<id>`. */
  source: string;
}

/** Non-secret per-channel settings (Telegram chat id, PagerDuty dedup key, …). */
export interface NotificationConfig {
  chatId?: string;
  dedupKey?: string;
}

export interface NotificationRequest {
  url: string;
  headers: Record<string, string>;
  body: unknown;
}

const SEVERITY_RANK: Record<NotificationSeverity, number> = { INFO: 0, WARNING: 1, CRITICAL: 2 };

/** Whether an alert of `severity` should reach a channel whose floor is `minSeverity`. */
export function meetsMinSeverity(
  severity: NotificationSeverity,
  minSeverity: NotificationSeverity,
): boolean {
  return SEVERITY_RANK[severity] >= SEVERITY_RANK[minSeverity];
}

const THEME_COLOR: Record<NotificationSeverity, string> = {
  INFO: '2563eb',
  WARNING: 'd97706',
  CRITICAL: 'dc2626',
};

/** PagerDuty's severity vocabulary differs from ours. */
const PAGERDUTY_SEVERITY: Record<NotificationSeverity, string> = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
};

const JSON_HEADERS = { 'content-type': 'application/json' } as const;

/** A one-line human summary shared by the chat-style providers. */
function line(payload: NotificationPayload): string {
  const head = `[${payload.severity}] ${payload.title}`;
  return payload.description ? `${head}\n${payload.description}` : head;
}

/**
 * Build the HTTP request for a channel. `secret` is the sensitive part (the webhook URL,
 * the bot token, or the routing key, depending on type); `config` is the non-secret
 * remainder. Throws if a type needs config it was not given (a Telegram channel with no
 * chat id), so a misconfigured channel fails loudly at send time rather than posting a
 * malformed request.
 */
export function buildNotificationRequest(
  type: NotificationChannelType,
  secret: string,
  config: NotificationConfig,
  payload: NotificationPayload,
): NotificationRequest {
  switch (type) {
    case 'webhook':
      return {
        url: secret,
        headers: { ...JSON_HEADERS },
        body: {
          severity: payload.severity,
          status: payload.status,
          title: payload.title,
          description: payload.description ?? '',
          source: payload.source,
        },
      };
    case 'slack':
      return { url: secret, headers: { ...JSON_HEADERS }, body: { text: line(payload) } };
    case 'discord':
      return { url: secret, headers: { ...JSON_HEADERS }, body: { content: line(payload) } };
    case 'teams':
      return {
        url: secret,
        headers: { ...JSON_HEADERS },
        body: {
          '@type': 'MessageCard',
          '@context': 'https://schema.org/extensions',
          themeColor: THEME_COLOR[payload.severity],
          summary: payload.title,
          text: line(payload),
        },
      };
    case 'telegram': {
      if (!config.chatId) throw new Error('telegram channel needs a chatId');
      return {
        url: `https://api.telegram.org/bot${secret}/sendMessage`,
        headers: { ...JSON_HEADERS },
        body: { chat_id: config.chatId, text: line(payload) },
      };
    }
    case 'pagerduty':
      return {
        url: 'https://events.pagerduty.com/v2/enqueue',
        headers: { ...JSON_HEADERS },
        body: {
          routing_key: secret,
          event_action: payload.status === 'RESOLVED' ? 'resolve' : 'trigger',
          ...(config.dedupKey ? { dedup_key: config.dedupKey } : {}),
          payload: {
            summary: payload.title,
            severity: PAGERDUTY_SEVERITY[payload.severity],
            source: payload.source,
          },
        },
      };
  }
}
